import React, { useEffect, useState } from 'react';
import { MailIcon, PhoneIcon, ShieldIcon, SendIcon, BuildingIcon, CalendarIcon, UserIcon, CheckIcon, ChevronRightIcon, ShieldCheckIcon } from 'lucide-react';
// Move service types and urgency levels to module scope
export const SERVICE_TYPES = [{
  id: 'objectbeveiliging',
  label: 'Objectbeveiliging',
  icon: <BuildingIcon className="w-5 h-5" data-id="element-92" />
}, {
  id: 'winkelbeveiliging',
  label: 'Winkelbeveiliging',
  icon: <ShieldIcon className="w-5 h-5" data-id="element-93" />
}, {
  id: 'evenementenbeveiliging',
  label: 'Evenementen',
  icon: <CalendarIcon className="w-5 h-5" data-id="element-94" />
}, {
  id: 'anders',
  label: 'Anders',
  icon: <CheckIcon className="w-5 h-5" data-id="element-95" />
}] as const;
export const URGENCY_LEVELS = [{
  value: 'laag',
  label: 'Laag - Planning voor de toekomst',
  color: 'bg-green-500'
}, {
  value: 'normaal',
  label: 'Normaal - Binnen enkele weken',
  color: 'bg-blue-500'
}, {
  value: 'hoog',
  label: 'Hoog - Zo snel mogelijk',
  color: 'bg-yellow-500'
}, {
  value: 'spoed',
  label: 'Spoed - Directe behoefte',
  color: 'bg-red-500'
}] as const;
// Helper functions to get labels
const getServiceTypeLabel = (id: string) => SERVICE_TYPES.find(s => s.id === id)?.label ?? id;
const getUrgencyLabel = (val: string) => URGENCY_LEVELS.find(u => u.value === val)?.label ?? val;
// Helper function to generate a random math question
const generateMathCaptcha = () => {
  const num1 = Math.floor(Math.random() * 10) + 1;
  const num2 = Math.floor(Math.random() * 10) + 1;
  return {
    question: `${num1} + ${num2} = ?`,
    answer: (num1 + num2).toString()
  };
};
// Generate a unique form token
const generateFormToken = () => {
  return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
};
// Check if the email is potentially suspicious
const isValidEmail = email => {
  // Basic email format validation
  const emailRegex = /^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}$/;
  if (!emailRegex.test(email)) return false;
  // Check for common spam domains (this list should be maintained)
  const suspiciousDomains = ['temp-mail.org', 'guerrillamail.com', 'mailinator.com', 'tempmail.com', 'yopmail.com', 'throwawaymail.com'];
  const domain = email.split('@')[1];
  return !suspiciousDomains.includes(domain);
};
// Check for suspicious patterns in message content
const containsSuspiciousContent = message => {
  const suspiciousPatterns = [/https?:\/\/(?!etaksoy\.nl)/i, /\b(viagra|cialis|buy now|casino|lottery|prize|winner)\b/i, /\b(crypto|bitcoin|ethereum|wallet|investment opportunity)\b/i // Crypto scam patterns
  ];
  return suspiciousPatterns.some(pattern => pattern.test(message));
};
// Time threshold for "filled too fast" detection - reduced for testing
const MIN_FILL_TIME_MS = typeof window !== 'undefined' && window.location.hostname === 'localhost' ? 1500 // 1.5 seconds in development
: 5000; // 5 seconds in production
// Rate limit settings - increased for testing
const RATE_LIMIT_COUNT = typeof window !== 'undefined' && window.location.hostname === 'localhost' ? 20 // 20 submissions in development
: 3; // 3 submissions in production
// Create an HTML email template for the contact form submission
const createEmailTemplate = data => {
  const {
    name,
    email,
    phone,
    company,
    serviceType,
    urgency,
    message,
    newsletter,
    date,
    metadata
  } = data;
  // Define CSS styles separately to avoid nesting template literals
  const styles = `
    body {
      font-family: Arial, sans-serif;
      line-height: 1.6;
      color: #333333;
      margin: 0;
      padding: 0;
      background-color: #f5f5f5;
    }
    .container {
      max-width: 600px;
      margin: 0 auto;
      background-color: #ffffff;
      border: 1px solid #e0e0e0;
    }
    .header {
      background-color: #1f2937;
      color: #ffffff;
      padding: 20px;
      text-align: center;
      border-bottom: 4px solid #ea580c;
    }
    .content {
      padding: 20px;
    }
    .section {
      margin-bottom: 20px;
      border-bottom: 1px solid #e0e0e0;
      padding-bottom: 15px;
    }
    .section:last-child {
      border-bottom: none;
    }
    .section-title {
      font-size: 16px;
      font-weight: bold;
      margin-bottom: 10px;
      color: #ea580c;
    }
    .field {
      margin-bottom: 10px;
    }
    .field-label {
      font-weight: bold;
      display: inline-block;
      width: 140px;
      color: #555555;
    }
    .field-value {
      display: inline-block;
    }
    .message-box {
      background-color: #f9f9f9;
      padding: 15px;
      border-left: 3px solid #ea580c;
      margin-top: 10px;
    }
    .footer {
      background-color: #f5f5f5;
      padding: 15px;
      text-align: center;
      font-size: 12px;
      color: #777777;
      border-top: 1px solid #e0e0e0;
    }
    .metadata {
      background-color: #f9f9f9;
      padding: 10px;
      font-size: 11px;
      color: #999999;
      margin-top: 15px;
      border: 1px solid #e0e0e0;
    }
    .urgency {
      display: inline-block;
      padding: 5px 10px;
      color: #ffffff;
      font-size: 12px;
      font-weight: bold;
      text-transform: uppercase;
    }
    .urgency-low {
      background-color: #22c55e;
    }
    .urgency-normal {
      background-color: #3b82f6;
    }
    .urgency-high {
      background-color: #eab308;
    }
    .urgency-urgent {
      background-color: #ef4444;
    }
  `;
  return `
<!DOCTYPE html>
<html lang="nl">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Nieuw contactformulier bericht</title>
  <style>
  ${styles}
</style>

</head>
<body>
  <div class="container">
    <div class="header">
      <h1 style="margin: 0; font-size: 24px;">Nieuw Contactformulier Bericht</h1>
      <p style="margin: 5px 0 0 0;">${date}</p>
    </div>
    <div class="content">
      <div class="section">
        <div class="section-title">Afzender Informatie</div>
        <div class="field">
          <span class="field-label">Naam:</span>
          <span class="field-value">${name}</span>
        </div>
        <div class="field">
          <span class="field-label">E-mail:</span>
          <span class="field-value">${email}</span>
        </div>
        ${phone ? `
        <div class="field">
          <span class="field-label">Telefoon:</span>
          <span class="field-value">${phone}</span>
        </div>
        ` : ''}
        ${company ? `
        <div class="field">
          <span class="field-label">Bedrijf:</span>
          <span class="field-value">${company}</span>
        </div>
        ` : ''}
      </div>
      <div class="section">
        <div class="section-title">Aanvraag Details</div>
        <div class="field">
          <span class="field-label">Type dienst:</span>
          <span class="field-value">${serviceType}</span>
        </div>
        <div class="field">
          <span class="field-label">Urgentie:</span>
          <span class="field-value">
            <span class="urgency urgency-${urgency === 'Laag - Planning voor de toekomst' ? 'low' : urgency === 'Normaal - Binnen enkele weken' ? 'normal' : urgency === 'Hoog - Zo snel mogelijk' ? 'high' : 'urgent'}">
              ${urgency}
            </span>
          </span>
        </div>
        <div class="field">
          <span class="field-label">Nieuwsbrief:</span>
          <span class="field-value">${newsletter}</span>
        </div>
      </div>
      <div class="section">
        <div class="section-title">Bericht</div>
        <div class="message-box">
          ${message.replace(/\n/g, '<br>')}
        </div>
      </div>
      <div class="metadata">
        <div><strong>Verzonden vanaf:</strong> ${metadata.url}</div>
        <div><strong>IP-adres:</strong> ${metadata.ip}</div>
        <div><strong>Website:</strong> etaksoy.nl</div>
        <div><strong>Datum en tijd:</strong> ${date}</div>
        <div><strong>Form ID:</strong> ${metadata.formId}</div>
      </div>
    </div>
    <div class="footer">
      <p>Dit bericht is verzonden via het contactformulier op etaksoy.nl</p>
      <p>Mede mogelijk gemaakt door <a href="https://radux.nl" style="color: #ea580c; text-decoration: none;">Radux.nl</a></p>
    </div>
  </div>
</body>
</html>
  `;
};
// CORS-safe send function with x-www-form-urlencoded format
const sendToMake = async formData => {
  console.log('Preparing form data for webhook');
  const webhookUrl = 'https://hook.eu2.make.com/ns0jmvus3udnm8grs6bbslj4d3rni753';
  // Get user's IP address if available
  const ipAddress = typeof window !== 'undefined' ? window.userIP || 'Niet beschikbaar' : 'Niet beschikbaar';
  // Get current URL
  const currentUrl = typeof window !== 'undefined' ? window.location.href : 'etaksoy.nl';
  // Get service type label using the module-scoped helper function
  const serviceTypeLabel = getServiceTypeLabel(formData.serviceType);
  // Get urgency level label using the module-scoped helper function
  const urgencyLabel = getUrgencyLabel(formData.urgency);
  // Format current date and time
  const formattedDate = new Date().toLocaleString('nl-NL', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
  // Create HTML email template
  const html = createEmailTemplate({
    name: formData.name,
    email: formData.email,
    phone: formData.phone || '',
    company: formData.company || '',
    serviceType: serviceTypeLabel,
    urgency: urgencyLabel,
    message: formData.message,
    newsletter: formData.newsletter ? 'Ja' : 'Nee',
    date: formattedDate,
    metadata: {
      ip: ipAddress,
      url: currentUrl,
      formId: formData.formToken
    }
  });
  // FLAT payload (no nested objects) for URLSearchParams
  const payload = {
    email_to: 'contact@etaksoy.nl',
    email_replyTo: formData.email,
    email_subject: `Nieuwe beveiligingsaanvraag: ${serviceTypeLabel} (${urgencyLabel})`,
    sender_name: formData.name,
    sender_email: formData.email,
    sender_phone: formData.phone || '',
    sender_company: formData.company || '',
    request_serviceType: serviceTypeLabel,
    request_urgency: urgencyLabel,
    request_message: formData.message,
    request_newsletter: formData.newsletter ? 'Ja' : 'Nee',
    meta_date: formattedDate,
    meta_ip: ipAddress,
    meta_url: currentUrl,
    meta_formId: formData.formToken,
    meta_userAgent: typeof window !== 'undefined' ? window.navigator.userAgent : 'Unknown',
    htmlContent: html // Complete HTML email body
  };
  // Convert to URL-encoded string
  const body = new URLSearchParams(payload).toString();
  console.log('Sending URL-encoded data to webhook');
  const response = await fetch(webhookUrl, {
    method: 'POST',
    headers: {
      // Simple request -> no preflight with this content type
      'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
    },
    body,
    mode: 'cors'
  });
  console.log('Webhook response status:', response.status);
  if (!response.ok) {
    throw new Error(`Webhook error: ${response.status} ${response.statusText}`);
  }
  return response;
};
export function ContactSection() {
  const initialCaptcha = generateMathCaptcha();
  // Create a submissions counter in localStorage if it doesn't exist
  useEffect(() => {
    if (!localStorage.getItem('formSubmissionCount')) {
      localStorage.setItem('formSubmissionCount', '0');
    }
    if (!localStorage.getItem('lastSubmissionTime')) {
      localStorage.setItem('lastSubmissionTime', '0');
    }
  }, []);
  const initialFormData = {
    name: '',
    email: '',
    phone: '',
    company: '',
    serviceType: 'objectbeveiliging',
    urgency: 'normaal',
    message: '',
    newsletter: false,
    // Anti-spam fields with better naming
    hp_timestamp: '',
    hp_reference: '',
    captcha: '',
    captchaQuestion: initialCaptcha.question,
    captchaAnswer: initialCaptcha.answer,
    formStartTime: Date.now(),
    formToken: generateFormToken(),
    acceptTerms: false // Terms acceptance checkbox
  };
  const [formData, setFormData] = useState(initialFormData);
  const [submitted, setSubmitted] = useState(false);
  const [focused, setFocused] = useState('');
  const [formStep, setFormStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState({
    email: '',
    captcha: '',
    message: '',
    general: ''
  });
  const handleChange = e => {
    const {
      name,
      value,
      type,
      checked
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear errors when user makes changes
    if (Object.keys(formErrors).includes(name)) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };
  const handleFocus = field => {
    setFocused(field);
  };
  const handleBlur = () => {
    setFocused('');
  };
  // Reset form with proper captcha generation
  const resetForm = () => {
    const newCaptcha = generateMathCaptcha();
    setFormData({
      ...initialFormData,
      captchaQuestion: newCaptcha.question,
      captchaAnswer: newCaptcha.answer,
      formStartTime: Date.now(),
      formToken: generateFormToken()
    });
    setFormStep(0);
  };
  // Validate form step and proceed to next
  const validateAndProceed = nextStep => {
    // Step 1 validation (personal info)
    if (formStep === 0) {
      // Validate email
      if (!isValidEmail(formData.email)) {
        setFormErrors(prev => ({
          ...prev,
          email: 'Vul een geldig e-mailadres in'
        }));
        return;
      }
      // Validate captcha
      if (formData.captcha !== formData.captchaAnswer) {
        setFormErrors(prev => ({
          ...prev,
          captcha: 'Het antwoord is niet correct'
        }));
        return;
      }
    }
    // All validations passed, proceed to next step
    setFormStep(nextStep);
  };
  const handleSubmit = async e => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      // Check for honeypot fields (should be empty)
      if (formData.hp_timestamp || formData.hp_reference) {
        console.error('Honeypot triggered:', {
          hp_timestamp: formData.hp_timestamp,
          hp_reference: formData.hp_reference
        });
        setFormErrors({
          ...formErrors,
          general: 'Ongeldige invoer gedetecteerd.'
        });
        setIsSubmitting(false);
        return;
      }
      // Check if form was filled out too quickly (likely a bot)
      const formFillTime = Date.now() - formData.formStartTime;
      console.log(`Form fill time: ${formFillTime}ms, minimum: ${MIN_FILL_TIME_MS}ms`);
      if (formFillTime < MIN_FILL_TIME_MS) {
        // Less than threshold time
        setFormErrors({
          ...formErrors,
          general: 'Formulier te snel ingevuld. Probeer het opnieuw.'
        });
        setIsSubmitting(false);
        return;
      }
      // Check for suspicious content
      if (containsSuspiciousContent(formData.message)) {
        setFormErrors({
          ...formErrors,
          message: 'Uw bericht bevat niet-toegestane inhoud'
        });
        setIsSubmitting(false);
        return;
      }
      // Rate limiting check - configurable limit per hour
      const submissionCount = parseInt(localStorage.getItem('formSubmissionCount') || '0');
      const lastSubmissionTime = parseInt(localStorage.getItem('lastSubmissionTime') || '0');
      const currentTime = Date.now();
      const oneHourInMs = 60 * 60 * 1000;
      // Reset counter if it's been more than an hour since last submission
      if (currentTime - lastSubmissionTime > oneHourInMs) {
        localStorage.setItem('formSubmissionCount', '1');
        localStorage.setItem('lastSubmissionTime', currentTime.toString());
      } else if (submissionCount >= RATE_LIMIT_COUNT) {
        // If more than limit submissions in the last hour
        setFormErrors({
          ...formErrors,
          general: 'Maximum aantal berichten bereikt. Probeer het later opnieuw.'
        });
        setIsSubmitting(false);
        return;
      } else {
        // Increment counter for submissions within the hour
        localStorage.setItem('formSubmissionCount', (submissionCount + 1).toString());
        localStorage.setItem('lastSubmissionTime', currentTime.toString());
      }
      try {
        // Use the enhanced email template send method
        await sendToMake(formData);
        // If we get here, the request was successful
        console.log('Webhook request completed successfully');
        // Add a small delay before showing success to make the transition feel more natural
        setTimeout(() => {
          setSubmitted(true);
          setIsSubmitting(false);
          // Reset form with proper captcha
          resetForm();
        }, 800);
        // Reset submission status after 5 seconds
        setTimeout(() => {
          setSubmitted(false);
        }, 5000);
      } catch (webhookError) {
        console.error('Error sending to webhook:', webhookError);
        throw webhookError; // Re-throw to be caught by the outer try/catch
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setFormErrors({
        ...formErrors,
        general: 'Er is een fout opgetreden bij het verzenden. Probeer het later opnieuw.'
      });
      setIsSubmitting(false);
    }
  };
  // Fixed animated label class with proper positioning
  const getLabelClass = field => {
    return `absolute left-14 transition-all duration-200 ${focused === field || formData[field] ? '-top-2.5 text-xs text-orange-500 bg-gray-800 px-2 z-10' : 'top-3 text-gray-400'}`;
  };
  // Input class with dynamic focus state
  const getInputClass = field => {
    const hasError = formErrors[field];
    return `w-full p-3 pl-14 bg-gray-700 border ${hasError ? 'border-red-500' : focused === field ? 'border-orange-500' : 'border-gray-600'} rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500`;
  };
  return <div className="bg-gray-900 py-12 md:py-16 border-t border-gray-800" data-id="element-96">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-97">
        <h2 className="text-2xl md:text-3xl font-bold mb-8 md:mb-10 flex items-center gap-2 justify-center" data-id="element-98">
          <ShieldIcon className="w-6 h-6 md:w-8 md:h-8 text-orange-500" data-id="element-99" />
          <span data-id="element-100">Neem Contact Op</span>
        </h2>
        <div className="grid md:grid-cols-2 gap-6 md:gap-10" data-id="element-101">
          {/* Contact Info - Simplified for mobile */}
          <div className="bg-gray-800 p-4 md:p-6 rounded-lg shadow-lg border border-gray-700 hover:border-gray-600 transition-colors" data-id="element-102">
            <h3 className="text-lg md:text-xl font-semibold mb-4 md:mb-6 border-b border-gray-700 pb-3 flex items-center gap-2" data-id="element-103">
              <ShieldIcon className="w-5 h-5 text-orange-500" data-id="element-104" />
              Contact Informatie
            </h3>
            <div className="space-y-4 md:space-y-6" data-id="element-105">
              {/* Contact details with smaller spacing for mobile */}
              <div className="flex items-center gap-3 md:gap-4 group" data-id="element-106">
                <div className="p-2 md:p-3 bg-gray-700 rounded-full group-hover:bg-orange-500/20 transition-colors" data-id="element-107">
                  <PhoneIcon className="w-5 h-5 md:w-6 md:h-6 text-orange-500" data-id="element-108" />
                </div>
                <div data-id="element-109">
                  <div className="font-medium text-gray-300 text-xs md:text-sm" data-id="element-110">
                    Telefoon
                  </div>
                  <div className="text-base md:text-xl group-hover:text-orange-500 transition-colors" data-id="element-111">
                    +31(6) 18023380
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3 md:gap-4 group" data-id="element-112">
                <div className="p-2 md:p-3 bg-gray-700 rounded-full group-hover:bg-orange-500/20 transition-colors" data-id="element-113">
                  <MailIcon className="w-5 h-5 md:w-6 md:h-6 text-orange-500" data-id="element-114" />
                </div>
                <div data-id="element-115">
                  <div className="font-medium text-gray-300 text-xs md:text-sm" data-id="element-116">
                    E-mail
                  </div>
                  <div className="text-base md:text-xl group-hover:text-orange-500 transition-colors" data-id="element-117">
                    contact@etaksoy.nl
                  </div>
                </div>
              </div>
            </div>
            {/* Services - Simplified grid for mobile */}
            <div className="mt-6 md:mt-8 space-y-3 md:space-y-4" data-id="element-118">
              <div className="text-base md:text-lg font-medium border-b border-gray-700 pb-2 flex items-center gap-2" data-id="element-119">
                <ShieldIcon className="w-4 h-4 md:w-5 md:h-5 text-orange-500" data-id="element-120" />
                Diensten
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 md:gap-3" data-id="element-121">
                <div className="flex items-center gap-2 bg-gray-700 p-2 rounded-md hover:bg-gray-600 transition-colors" data-id="element-122">
                  <CheckIcon className="w-4 h-4 md:w-5 md:h-5 text-green-500" data-id="element-123" />
                  <span data-id="element-124">Objectbeveiliging</span>
                </div>
                <div className="flex items-center gap-2 bg-gray-700 p-2 rounded-md hover:bg-gray-600 transition-colors" data-id="element-125">
                  <CheckIcon className="w-4 h-4 md:w-5 md:h-5 text-green-500" data-id="element-126" />
                  <span data-id="element-127">Winkelbeveiliging</span>
                </div>
                <div className="flex items-center gap-2 bg-gray-700 p-2 rounded-md hover:bg-gray-600 transition-colors" data-id="element-128">
                  <CheckIcon className="w-4 h-4 md:w-5 md:h-5 text-green-500" data-id="element-129" />
                  <span data-id="element-130">Evenementen</span>
                </div>
                <div className="flex items-center gap-2 bg-gray-700 p-2 rounded-md hover:bg-gray-600 transition-colors" data-id="element-131">
                  <CheckIcon className="w-4 h-4 md:w-5 md:h-5 text-green-500" data-id="element-132" />
                  <span data-id="element-133">Toegangscontrole</span>
                </div>
              </div>
            </div>
          </div>
          {/* Contact Form - Mobile optimized */}
          <div className="bg-gray-800 p-4 md:p-6 rounded-lg shadow-lg border border-gray-700 hover:border-gray-600 transition-colors" data-id="element-134">
            {submitted ? <div className="bg-green-500/20 border border-green-500 p-6 rounded-lg text-center h-full flex flex-col items-center justify-center animate-fadeIn" data-id="element-135">
                <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-green-500/30 flex items-center justify-center mb-4" data-id="element-136">
                  <ShieldIcon className="w-8 h-8 md:w-10 md:h-10 text-green-500" data-id="element-137" />
                </div>
                <p className="text-green-300 font-medium text-lg md:text-xl mb-2" data-id="element-138">
                  Bedankt voor uw bericht!
                </p>
                <p className="text-green-200 text-sm md:text-base" data-id="element-139">
                  We nemen zo spoedig mogelijk contact met u op.
                </p>
              </div> : <form onSubmit={handleSubmit} className="space-y-4 md:space-y-5" data-id="element-140">
                <h3 className="text-lg md:text-xl font-semibold mb-4 md:mb-6 border-b border-gray-700 pb-3 flex items-center gap-2" data-id="element-141">
                  <MailIcon className="w-5 h-5 text-orange-500" data-id="element-142" />
                  Stuur een bericht
                </h3>
                {/* Improved honeypot fields - truly invisible and non-autofillable */}
                <div style={{
              display: 'none'
            }} aria-hidden="true" data-id="element-143">
                  <label htmlFor="hp_timestamp" data-id="element-144">Timestamp</label>
                  <input type="text" id="hp_timestamp" name="hp_timestamp" tabIndex={-1} autoComplete="new-password" value={formData.hp_timestamp || ''} onChange={handleChange} data-id="element-145" />
                  <label htmlFor="hp_reference" data-id="element-146">Reference</label>
                  <input type="text" id="hp_reference" name="hp_reference" tabIndex={-1} autoComplete="new-password" value={formData.hp_reference || ''} onChange={handleChange} data-id="element-147" />
                </div>
                {/* Form token - to prevent CSRF attacks */}
                <input type="hidden" name="formToken" value={formData.formToken} data-id="element-148" />
                {/* Time tracker - to prevent rapid form submissions */}
                <input type="hidden" name="formStartTime" value={formData.formStartTime} data-id="element-149" />
                {/* Simplified Progress Indicator for mobile - fixed to prevent validation skipping */}
                <div className="flex justify-between mb-4 md:mb-6" data-id="element-150">
                  <div className="flex space-x-2" data-id="element-151">
                    {[0, 1, 2].map(step => <button key={step} type="button" onClick={() => step <= formStep ? setFormStep(step) : validateAndProceed(step)} className={`w-2 h-2 md:w-3 md:h-3 rounded-full ${formStep >= step ? 'bg-orange-500' : 'bg-gray-600'} transition-colors`} aria-label={`Step ${step + 1}`} disabled={step > formStep} data-id="element-152" />)}
                  </div>
                  <div className="text-xs md:text-sm text-gray-400" data-id="element-153">
                    Stap {formStep + 1}/3
                  </div>
                </div>
                {/* Step 1: Basic Info - Simplified for mobile */}
                <div className={formStep === 0 ? 'block' : 'hidden'} data-id="element-154">
                  {/* Name Field */}
                  <div className="relative mb-4" data-id="element-155">
                    <label htmlFor="name" className={getLabelClass('name')} data-id="element-156">
                      Naam
                    </label>
                    <div className="absolute left-0 top-0 h-full flex items-center justify-center px-4 pointer-events-none" data-id="element-157">
                      <UserIcon className="w-5 h-5 text-orange-500" data-id="element-158" />
                    </div>
                    <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} onFocus={() => handleFocus('name')} onBlur={handleBlur} className={getInputClass('name')} placeholder=" " required maxLength={50} // Prevent extremely long inputs
                pattern="^[^<>]*$" // Prevent HTML injection
                data-id="element-159" />
                  </div>
                  {/* Email Field */}
                  <div className="relative mb-4" data-id="element-160">
                    <label htmlFor="email" className={getLabelClass('email')} data-id="element-161">
                      E-mail
                    </label>
                    <div className="absolute left-0 top-0 h-full flex items-center justify-center px-4 pointer-events-none" data-id="element-162">
                      <MailIcon className="w-5 h-5 text-orange-500" data-id="element-163" />
                    </div>
                    <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} onFocus={() => handleFocus('email')} onBlur={handleBlur} className={getInputClass('email')} placeholder=" " required pattern="[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}$" // Basic email validation
                maxLength={100} data-id="element-164" />
                    {formErrors.email && <p className="text-red-500 text-xs mt-1" data-id="element-165">
                        {formErrors.email}
                      </p>}
                  </div>
                  {/* Phone Field */}
                  <div className="relative mb-4" data-id="element-166">
                    <label htmlFor="phone" className={getLabelClass('phone')} data-id="element-167">
                      Telefoon
                    </label>
                    <div className="absolute left-0 top-0 h-full flex items-center justify-center px-4 pointer-events-none" data-id="element-168">
                      <PhoneIcon className="w-5 h-5 text-orange-500" data-id="element-169" />
                    </div>
                    <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} onFocus={() => handleFocus('phone')} onBlur={handleBlur} className={getInputClass('phone')} placeholder=" " pattern="^[0-9+\-\s()]*$" // Only allow valid phone characters
                maxLength={20} data-id="element-170" />
                  </div>
                  {/* Simple math captcha */}
                  <div className="mb-4" data-id="element-171">
                    <div className="flex items-center mb-2" data-id="element-172">
                      <ShieldCheckIcon className="w-5 h-5 text-orange-500 mr-2" data-id="element-173" />
                      <label htmlFor="captcha" className="block text-base font-medium text-orange-500" data-id="element-174">
                        Verificatie: {formData.captchaQuestion}
                      </label>
                    </div>
                    <div className="relative" data-id="element-175">
                      <div className="absolute left-0 top-0 h-full flex items-center justify-center px-4 pointer-events-none" data-id="element-176">
                        <ShieldCheckIcon className="w-5 h-5 text-orange-500" data-id="element-177" />
                      </div>
                      <input type="text" id="captcha" name="captcha" value={formData.captcha} onChange={handleChange} className="w-full p-3 pl-14 bg-gray-700 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500" placeholder="Vul het antwoord in" required inputMode="numeric" maxLength={3} data-id="element-178" />
                    </div>
                    {formErrors.captcha && <p className="text-red-500 text-xs mt-1" data-id="element-179">
                        {formErrors.captcha}
                      </p>}
                  </div>
                  <button type="button" onClick={() => validateAndProceed(1)} className="w-full mt-4 py-3 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-md transition-colors flex items-center justify-center gap-2" disabled={isSubmitting} data-id="element-180">
                    <span data-id="element-181">Volgende</span>
                    <ChevronRightIcon className="w-5 h-5" data-id="element-182" />
                  </button>
                </div>
                {/* Step 2: Service Details - Simplified for mobile */}
                <div className={formStep === 1 ? 'block' : 'hidden'} data-id="element-183">
                  {/* Service Type Field - Simplified for mobile */}
                  <div className="space-y-3 mb-4" data-id="element-184">
                    <label className="block text-base md:text-lg font-medium text-orange-500" data-id="element-185">
                      Type dienst
                    </label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-id="element-186">
                      {SERVICE_TYPES.map(service => <label key={service.id} className={`flex items-center p-2 md:p-3 gap-2 md:gap-3 border-2 rounded-lg cursor-pointer transition-all ${formData.serviceType === service.id ? 'border-orange-500 bg-orange-500/10' : 'border-gray-600 hover:border-gray-500'}`} data-id="element-187">
                          <input type="radio" name="serviceType" value={service.id} checked={formData.serviceType === service.id} onChange={handleChange} className="sr-only" data-id="element-188" />
                          <div className={`p-1 md:p-2 rounded-full ${formData.serviceType === service.id ? 'bg-orange-500 text-white' : 'bg-gray-700 text-gray-400'}`} data-id="element-189">
                            {service.icon}
                          </div>
                          <span className="text-sm md:text-base" data-id="element-190">
                            {service.label}
                          </span>
                        </label>)}
                    </div>
                  </div>
                  {/* Urgency Field - Simplified for mobile */}
                  <div className="space-y-3" data-id="element-191">
                    <label className="block text-base md:text-lg font-medium text-orange-500" data-id="element-192">
                      Urgentie
                    </label>
                    <div className="space-y-2" data-id="element-193">
                      {URGENCY_LEVELS.map(level => <label key={level.value} className={`flex items-center p-2 md:p-3 gap-2 md:gap-3 border-2 rounded-lg cursor-pointer transition-all ${formData.urgency === level.value ? 'border-orange-500 bg-orange-500/10' : 'border-gray-600 hover:border-gray-500'}`} data-id="element-194">
                          <input type="radio" name="urgency" value={level.value} checked={formData.urgency === level.value} onChange={handleChange} className="sr-only" data-id="element-195" />
                          <div className={`w-3 h-3 rounded-full ${level.color}`} data-id="element-196"></div>
                          <span className="text-sm md:text-base" data-id="element-197">
                            {level.label}
                          </span>
                        </label>)}
                    </div>
                  </div>
                  <div className="flex justify-between mt-4 space-x-2 md:space-x-3" data-id="element-198">
                    <button type="button" onClick={() => setFormStep(0)} className="py-2 md:py-3 px-3 md:px-5 bg-gray-700 hover:bg-gray-600 text-white font-medium rounded-md transition-colors text-sm md:text-base" data-id="element-199">
                      Terug
                    </button>
                    <button type="button" onClick={() => validateAndProceed(2)} className="flex-grow py-2 md:py-3 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-md transition-colors flex items-center justify-center gap-2 text-sm md:text-base" data-id="element-200">
                      <span data-id="element-201">Volgende</span>
                      <ChevronRightIcon className="w-5 h-5" data-id="element-202" />
                    </button>
                  </div>
                </div>
                {/* Step 3: Message - Simplified for mobile */}
                <div className={formStep === 2 ? 'block' : 'hidden'} data-id="element-203">
                  {/* Message Field */}
                  <div className="relative mb-4" data-id="element-204">
                    <label className="block text-base md:text-lg font-medium text-orange-500 mb-2" data-id="element-205">
                      Uw bericht
                    </label>
                    <textarea id="message" name="message" value={formData.message} onChange={handleChange} rows={4} className="w-full p-3 md:p-4 bg-gray-700 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500" placeholder="Beschrijf uw beveiligingsbehoefte of vraag..." required maxLength={1000} // Limit message length
                data-id="element-206"></textarea>
                    {formErrors.message && <p className="text-red-500 text-xs mt-1" data-id="element-207">
                        {formErrors.message}
                      </p>}
                  </div>
                  {/* Newsletter Checkbox - Simplified for mobile */}
                  <div className="flex items-center space-x-2 md:space-x-3 p-2 md:p-3 bg-gray-700 rounded-lg hover:bg-gray-650 transition-colors" data-id="element-208">
                    <input type="checkbox" id="newsletter" name="newsletter" checked={formData.newsletter} onChange={handleChange} className="h-4 w-4 md:h-5 md:w-5 text-orange-500 focus:ring-orange-500 border-gray-600 rounded" data-id="element-209" />
                    <label htmlFor="newsletter" className="text-sm md:text-base text-gray-300 cursor-pointer" data-id="element-210">
                      Houd mij op de hoogte
                    </label>
                  </div>
                  {/* Terms acceptance - additional spam prevention */}
                  <div className="flex items-center space-x-2 md:space-x-3 p-2 md:p-3 bg-gray-700 rounded-lg hover:bg-gray-650 transition-colors mt-3" data-id="element-211">
                    <input type="checkbox" id="acceptTerms" name="acceptTerms" checked={formData.acceptTerms} onChange={handleChange} className="h-4 w-4 md:h-5 md:w-5 text-orange-500 focus:ring-orange-500 border-gray-600 rounded" required data-id="element-212" />
                    <label htmlFor="acceptTerms" className="text-sm md:text-base text-gray-300 cursor-pointer" data-id="element-213">
                      Ik ben geen robot en ga akkoord met de voorwaarden
                    </label>
                  </div>
                  {formErrors.general && <div className="p-3 bg-red-500/20 border border-red-500 rounded-md mt-3" data-id="element-214">
                      <p className="text-red-300 text-sm" data-id="element-215">
                        {formErrors.general}
                      </p>
                    </div>}
                  <div className="flex justify-between mt-4 space-x-2 md:space-x-3" data-id="element-216">
                    <button type="button" onClick={() => setFormStep(1)} className="py-2 md:py-3 px-3 md:px-5 bg-gray-700 hover:bg-gray-600 text-white font-medium rounded-md transition-colors text-sm md:text-base" data-id="element-217">
                      Terug
                    </button>
                    <button type="submit" className="flex-grow py-2 md:py-3 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-md transition-colors flex items-center justify-center gap-2 text-sm md:text-base relative overflow-hidden" disabled={isSubmitting} data-id="element-218">
                      {isSubmitting ? <div className="flex items-center justify-center" data-id="element-219">
                          <div className="animate-spin mr-2 h-5 w-5 border-t-2 border-b-2 border-white rounded-full" data-id="element-220"></div>
                          <span data-id="element-221">Verzenden...</span>
                        </div> : <>
                          <SendIcon className="w-4 h-4 md:w-5 md:h-5" data-id="element-222" />
                          <span data-id="element-223">Verstuur</span>
                        </>}
                      {isSubmitting && <div className="absolute bottom-0 left-0 h-1 bg-white animate-progress" data-id="element-224"></div>}
                    </button>
                  </div>
                </div>
              </form>}
          </div>
        </div>
      </div>
    </div>;
}